import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CampaignIcon from "@mui/icons-material/Campaign";
import CampaignFileDetail from "../components/CampaignFileDetail";
import { useState } from "react";
import CampaignUpload from "../components/CampaignUpload";
import RenderOnRole from "../components/RenderOnRole";

async function fetchCampaigns() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/campaigns`
  );
  return response;
}

export default function CampaingsConfig() {
  const [campaignFileDetail, setCampaignFileDetail] = useState();
  const [openUploadDlg, setOpenUploadDlg] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const columns = [
    { field: "id", headerName: "id", width: 10, hide: true },
    { field: "employerName", headerName: "Employer", width: 200 },
    { field: "filename", headerName: "Filename", width: 350 },
    {
      field: "processStatus",
      headerName: "Status",
      width: 150,
      editable: false,
    },
    {
      field: "numOfRecords",
      headerName: "Number of records",
      width: 150,
      editable: false,
    },
    {
      field: "viewDetails",
      headerName: "View Details",
      width: 100,
      renderCell: (params) => (
        <div key={"action." + params.id}>
          <IconButton
            key={"view." + params.id}
            onClick={() => {
              setCampaignFileDetail({ ...params.row });
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  const { data, isLoading } = useQuery(["campaigns"], fetchCampaigns);

  const queryClient = useQueryClient();

  const uploadDialog = () => {
    return (
      <Dialog open={openUploadDlg} fullWidth maxWidth="lg" onClose={() => {}}>
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <UploadFileIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              Upload Campaign File
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setOpenUploadDlg(false);
              queryClient.invalidateQueries({ queryKey: ["campaigns"] });
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ paddingTop: 3 }}>
          <CampaignUpload />
        </DialogContent>
      </Dialog>
    );
  };

  const campaignFileDetailDialog = (event) => {
    return (
      <Dialog
        open={campaignFileDetail !== "undefined"}
        fullWidth
        onClose={() => {}}
        maxWidth="xl"
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CampaignIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              Campaign File Detail
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setCampaignFileDetail();
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <CampaignFileDetail campaignInfo={campaignFileDetail} />
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {isLoading ? (
        ""
      ) : (
        <Grid container p={0} m={0} spacing={0}>
          <Grid item xs={12}>
            <Box display="flex">
              <Typography variant="h5">Campaigns</Typography>
              <RenderOnRole roles={["create-campaign"]}>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ marginLeft: "10px" }}
                  onClick={() => setOpenUploadDlg(true)}
                >
                  Upload Campaign
                </Button>
              </RenderOnRole>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ width: "100%", height: "80vh" }}>
            <DataGrid
              rows={data.data}
              columns={columns}
              getRowHeight={() => 25}
              density="compact"
              disableDensitySelector
              disableColumnSelector
              disableColumnFilter
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Grid>
        </Grid>
      )}
      {campaignFileDetail ? campaignFileDetailDialog() : ""}
      {openUploadDlg ? uploadDialog() : ""}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
